/* eslint-disable react/jsx-props-no-spreading */
import React, {
  useState, useEffect, useRef,
} from 'react';
import {
  Fab,
  useForm,
  RichTextEditor,
  EditorStateHandle,
  TemplatePluginHandle,
  getSubmitButtonProps,
} from '@marageti/z4-lib';
import {
  Box,
  Container,
  Button,
  Alert,
  AlertTitle,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
} from '@mui/material';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { LoadingButton } from '@mui/lab';
import { Controller } from 'react-hook-form';
import ChooseTemplateDialog from '../../../../components/choose-template-dialog';

const schema = z.object({
  assignedTo: z.string().min(1, 'Required'),
  message: z.string().min(1, 'Required'),
});

type ScreeningFormProps = {
  onSubmit: (data: any) => void;
  onCancel: () => void
  error: boolean;
  defaultValues: {
    assignedTo: string | undefined;
    message: string | undefined;
  };
  draftResetKey?: number;
};

const ScreeningForm = ({
  onSubmit,
  onCancel,
  error,
  defaultValues,
  draftResetKey = 0,
}: ScreeningFormProps) => {
  const {
    handleSubmit,
    formState: { errors, isSubmitting, isValid },
    reset,
    control,
    getValues,
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues,
  });
  const editorRef = useRef<EditorStateHandle>(null);
  const templatePluginRef = useRef<TemplatePluginHandle>(null);
  const [isIntentionalSubmit, setIsIntentionalSubmit] = useState(false);

  const setTemplateValue = (value: string) => {
    if (templatePluginRef.current) {
      templatePluginRef.current.insertTemplate(value);
    }
  };

  const [showChooseTemplate, setShowChooseTemplate] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isAccidentalSubmit, setIsAccidentalSubmit] = useState(false);
  const [accidentalSubmitInfo, setAccidentalSubmitInfo] = useState<any>(null);

  // Function to prevent accidental submits with detailed logging
  const preventAccidentalSubmit = (event: React.FormEvent) => {
    // Detailed log for debugging all submit attempts
    const now = Date.now();
    const debugInfo = {
      timestamp: new Date().toISOString(),
      eventType: event.type,
      isTrusted: event.isTrusted, // true = user interaction, false = programmatic

      // Enhanced event details
      eventDetails: {
        bubbles: event.bubbles,
        cancelable: event.cancelable,
        defaultPrevented: event.defaultPrevented,
        eventPhase: event.eventPhase,
        timeStamp: event.timeStamp,
        submitter: (event as any).submitter?.tagName || null,
      },

      target: {
        tagName: (event.target as HTMLElement)?.tagName,
        type: (event.target as HTMLInputElement)?.type,
        className: (event.target as HTMLElement)?.className,
        id: (event.target as HTMLElement)?.id,
      },
      currentTarget: {
        tagName: (event.currentTarget as HTMLElement)?.tagName,
        className: (event.currentTarget as HTMLElement)?.className,
      },
      activeElement: {
        tagName: document.activeElement?.tagName,
        className: document.activeElement?.className,
        id: document.activeElement?.id,
      },

      // Enhanced form state
      formState: {
        isValid,
        isSubmitting,
        hasErrors: Object.keys(errors).length > 0,
        errorCount: Object.keys(errors).length,
        errorFields: Object.keys(errors),
      },

      // User interaction analysis
      interactionAnalysis: {
        intentionalSubmit: isIntentionalSubmit,
        documentHasFocus: document.hasFocus(),
        pageVisible: document.visibilityState === 'visible',
        timeSinceLoad: now - (window.performance?.timing?.loadEventEnd || now),
        userAgent: navigator.userAgent,
      },

      // Potential causes analysis
      suspiciousIndicators: {
        programmaticEvent: !event.isTrusted,
        noDocumentFocus: !document.hasFocus(),
        noUserIntent: !isIntentionalSubmit,
        hiddenPage: document.visibilityState !== 'visible',
        quickSubmission: (event.timeStamp || now) < 1000, // Less than 1 second since page interaction
      },

      // Browser and environment
      environment: {
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight,
        },
        keyboardModifiers: {
          altKey: (event as any).altKey || false,
          ctrlKey: (event as any).ctrlKey || false,
          metaKey: (event as any).metaKey || false,
          shiftKey: (event as any).shiftKey || false,
        },
        documentState: {
          hasFocus: document.hasFocus(),
          visibilityState: document.visibilityState,
          readyState: document.readyState,
          referrer: document.referrer ? new URL(document.referrer).hostname : 'direct',
        },
      },

      // Stack trace for debugging
      stackTrace: new Error('AccidentalSubmit').stack?.split('\n').slice(1, 6).join('\n') || 'N/A',
    };

    if (!isIntentionalSubmit) {
      // Log the accidental submit attempt
      // eslint-disable-next-line no-console
      console.warn('🚨 SUBMIT BLOCKED - Unintentional submit attempt:', debugInfo);

      // Simple log for easy identification in production
      // eslint-disable-next-line no-console
      console.warn(`🚨 ACCIDENTAL SUBMIT BLOCKED: ${event.type} event from ${debugInfo.target.tagName} at ${debugInfo.timestamp}`);

      // Show modal with debugging information
      setAccidentalSubmitInfo(debugInfo);
      setIsAccidentalSubmit(true);
      setShowConfirmModal(true);

      event.preventDefault();
      return false;
    }

    // Log the intentional successful submit
    // eslint-disable-next-line no-console
    console.log('✅ SUBMIT ALLOWED - Intentional submit:', debugInfo);

    return true;
  };

  // Function to show the confirmation modal
  const handleFormConfirmation = () => {
    // eslint-disable-next-line no-console
    console.log('handleFormConfirmation');
    setIsAccidentalSubmit(false);
    setAccidentalSubmitInfo(null);
    setShowConfirmModal(true);
  };

  // Function to confirm and send
  const handleConfirmSend = () => {
    setShowConfirmModal(false);
    setIsAccidentalSubmit(false);
    setAccidentalSubmitInfo(null);
    setIsIntentionalSubmit(true);
    // Get the current form data and submit it
    handleSubmit(onSubmit)();
  };

  // Function for submit button click
  const handleSubmitButtonClick = () => {
    setIsAccidentalSubmit(false);
    setAccidentalSubmitInfo(null);
    setShowConfirmModal(true);
  };

  // Function to close modal and reset states
  const handleCloseModal = () => {
    setShowConfirmModal(false);
    setIsAccidentalSubmit(false);
    setAccidentalSubmitInfo(null);
  };

  // Function to copy debug info to clipboard
  const handleCopyDebugInfo = async () => {
    if (accidentalSubmitInfo) {
      try {
        const debugText = JSON.stringify(accidentalSubmitInfo, null, 2);
        await navigator.clipboard.writeText(debugText);
        // eslint-disable-next-line no-console
        console.log('Debug info copied to clipboard');
      } catch (clipboardError) {
        // eslint-disable-next-line no-console
        console.error('Failed to copy debug info:', clipboardError);
      }
    }
  };

  useEffect(() => {
    const { message: currentMessage } = getValues();

    if (!currentMessage && defaultValues.message) { // prevent screening message from overwriting composed message
      editorRef.current?.setEditorState(defaultValues.message);
      reset(defaultValues);
    }
  }, [defaultValues, getValues, reset]);

  const draftMessageRef = useRef(defaultValues.message);
  draftMessageRef.current = defaultValues.message;

  useEffect(() => {
    if (draftResetKey === 0) {
      return;
    }

    const nextMessage = draftMessageRef.current ?? '';
    editorRef.current?.setEditorState(nextMessage);
    reset({
      ...getValues(),
      message: nextMessage,
    });
  }, [draftResetKey, getValues, reset]);

  return (
    <>
      <form
        onSubmit={(event) => {
          if (preventAccidentalSubmit(event)) {
            handleSubmit(handleFormConfirmation)(event);
          }
        }}
      >
        <Controller
          control={control}
          name="message"
          rules={{ maxLength: 6000 }}
          render={({ field }) => (
            <RichTextEditor
              defaultValue={defaultValues.message}
              editorName="Screen-Message"
              editorStateRef={editorRef}
              error={!!errors.message}
              helperText={errors.message?.message}
              insertTemplateProps={{
                onShowTemplateModal: () => setShowChooseTemplate(true),
                ref: templatePluginRef,
              }}
              label="Message"
              onChange={field.onChange}
              useLinks
              useLists
              sx={{
                minHeight: { desktop: '500px' },
                flex: 1,
              }}
            />
          )}
        />

        <ChooseTemplateDialog
          closeDialog={() => setShowChooseTemplate(false)}
          onSubmit={setTemplateValue}
          open={showChooseTemplate}
          submitButtonText="Insert"
        />

        <Box aria-hidden sx={{ height: { mobile: (5 + 10.5) * 8, desktop: 0 } }} />

        <Fab>
          <Container sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: '20px',
            flexDirection: {
              mobile: 'column',
              desktop: 'row-reverse',
            },
          }}
          >
            <Button
              variant="outlined"
              onClick={onCancel}
              sx={{
                width: { desktop: 'fit-content' },
              }}
            >
              Cancel
            </Button>
            <Tooltip
              title={!defaultValues.assignedTo ? 'Please assign this trip to send a message' : ''}
              placement="top"
            >
              <span>
                <LoadingButton
                  type="button"
                  variant="contained"
                  sx={{
                    width: { desktop: 'fit-content', mobile: '100%' },
                  }}
                  onClick={handleSubmitButtonClick}
                  {...getSubmitButtonProps(isSubmitting, isValid)}
                >
                  Send Message
                </LoadingButton>
              </span>
            </Tooltip>
          </Container>
        </Fab>

        {error && (
        <Alert severity="error">
          <AlertTitle>There was an error submitting screening message</AlertTitle>
          Please try again later.
        </Alert>
        )}
      </form>

      <Dialog
        open={showConfirmModal}
        onClose={handleCloseModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {isAccidentalSubmit ? 'Accidental Submit Detected' : 'Confirmation'}
        </DialogTitle>
        <DialogContent>
          {isAccidentalSubmit ? (
            <div>
              <Typography variant="body1" sx={{ mb: 2 }}>
                An event tried to submit this form automatically. This has been blocked.
                <strong> Event details: </strong>
                {`${accidentalSubmitInfo?.eventType} from ${accidentalSubmitInfo?.target?.tagName}`}
              </Typography>
              <Typography variant="body1">
                Would you like to send the message anyway?
              </Typography>
            </div>
          ) : (
            <Typography>
              Are you sure you want to send this screening message to the traveler?
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseModal}
            color="inherit"
          >
            Cancel
          </Button>
          {isAccidentalSubmit && (
            <Button
              onClick={handleCopyDebugInfo}
              variant="outlined"
              color="info"
            >
              Copy Debug Info
            </Button>
          )}
          <Button
            onClick={handleConfirmSend}
            variant="contained"
            color={isAccidentalSubmit ? 'error' : 'primary'}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Sending...' : 'Send Message'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ScreeningForm;
